import React, { useEffect, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import axios from 'axios';
import _ from 'lodash';

import { useApp } from '../_libs/hooks/useApp';
import { useBrand } from '../_libs/hooks/useBrand';

// import { OrganizationBrandData, ReportDataType } from '../_libs/app/types';
import newFileWithISOName from '../_libs/utils/newFileWithISOName';
import uploadLogoFileWithOptions from '../_libs/utils/uploadLogoFileWithOptions';

import LogoDragAndDrop from './LogoDragAndDrop';

const LogoUploaderAndColorPicker = ({
  confirmationCallback,
}: {
  confirmationCallback?: () => void;
}): JSX.Element => {
  const {
    reportData,
    userId,
  } = useApp();
  const {
    coBrand,
    primaryBrand,
    setPrimaryBrand,
  } = useBrand();
  const methods = useForm();
  const [logoFileName, setLogoFileName] = useState<string>();
  const [isAbleToConfirm, setIsAbleToConfirm] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  function handleFileChange (
    file: File,
    // fieldName: string,
  ) {
    if (file && file.size > 0) {
      setIsAbleToConfirm(true);
      setLogoFileName(file.name);
    } else {
      // If file is not empty, assume it is a real file
      setIsAbleToConfirm(false);
      setLogoFileName(null);
      // methods.setError(fieldName, {
      //   type: 'custom',
      //   message: 'Please select a file to upload.',
      // });
    }
  }

  useEffect(() => {
    methods.watch((data, { name, type }) => {
      // When values are updated via setValue, type === undefined
      if (name === 'file') {
        const file: File = data.file;
        handleFileChange(file);
      }
      // Format data for the following fields
      const validValues = [
        'color',
        'logo_height',
        'logo_width',
      ];
      if (validValues.indexOf(name) > -1) {
        // Update backend Organization
        axios.post('/update_organization/', {
          userId,
          organizationId: primaryBrand.id,
          coBrandId: coBrand.id,
          reportId: reportData.id,
          key: (name === 'color') ? 'brand_color' : name,
          value: data[name],
        });
      }
      if (type === 'change') {
        if (name === 'file_upload') {
          if (!data.file_upload) {
            return;
          }
          // data.file is a FileList; get the first file
          const file: File = data.file_upload[0];
          handleFileChange(file);
        }
      }
    });
  }, []);

  async function handleConfirm () {
    const data: FieldValues = methods.getValues();
    setIsUploading(true);

    if (data.file_upload || data.file) {
      const file: File = data.file ? data.file : data.file_upload[0];
      if (file.size < 0) {
        methods.setError('file_upload', {
          type: 'custom',
          message: 'It appears that no logo file has been selected. Please select a file first.',
        });
        setIsAbleToConfirm(false);
        return;
      }
      const fileTypeMatch = file.name.toLowerCase().match(/^.*\.(jpeg|jpg|png)$/);
      if (fileTypeMatch.length === 0) {
        methods.setError('file_upload', {
          type: 'custom',
          message: `File type ${fileTypeMatch[1]} is not supported. Please select a .jpeg, .jpg, or .png.`,
        });
        setIsUploading(false);
      } else {
        const _file = newFileWithISOName(file);
        const logo_url = await uploadLogoFileWithOptions(
          _file,
          {
            container: '',
            folderPath: 'logos',
          },
        );
        if (logo_url) {
          // Update the organization's logo
          axios.post('/update_organization/', {
            // userId,
            reportId: reportData.id,
            organizationId: primaryBrand.id,
            coBrandId: coBrand.id,
            key: 'logo_url',
            value: logo_url,
          });

          // Update the logo data for the primary band
          const _primaryBrand = _.cloneDeep(primaryBrand);
          _primaryBrand.logoUrl = logo_url;
          _primaryBrand.logoHeight = data.logo_height;
          _primaryBrand.logoWidth = data.logo_width;
          setPrimaryBrand(_primaryBrand);

          // Call the confirmation callback
          if (confirmationCallback) {
            confirmationCallback();
          }
          setIsUploading(false);
        } else {
          methods.setError('file_upload', {
            type: 'custom',
            message: 'Could not upload logo. Please try again.',
          });
          setIsUploading(false);
        }
      }
    }
  }

  /**
   *   
      NTH pre-launch:
      // If there's a brand, pre-upload the logo and the colors
      useEffect(() => {
        if (primaryBrand !== null) {
          if (primaryBrand.logoUrl) {
            setLogoUrl(primaryBrand.logoUrl);
          }
          if (primaryBrand.colors) {
            setColor(primaryBrand.colors.primary);
          }
        }
      }, [primaryBrand]);
   */

  return (
    <FormProvider {...methods}>
      <form className="mb-4">
        <h1 className="f-serif f-normal">
          Upload a Logo for <Link className="nowrap f-italic" to="/organization/">{primaryBrand.name}</Link>.
        </h1>
        <p>For best results, use a logo that's 172 x 60. Only .png, .jpg, or .jpeg files accepted.</p>
        <div>
          <label className="button f-sans inline-block mb-4">
            Browse files...
            <input
              accept=".png, .jpg, .jpeg"
              style={{ opacity: 0 }}
              type="file"
              {
                ...methods.register(
                  'file_upload',
                  { required: (!primaryBrand.logoUrl) }
                )
              }
            />
          </label>
          {
            logoFileName
              && <span className="pl-2">../{logoFileName}</span>
          }
        </div>
        <div className="f-small mb-4">OR</div>
        <LogoDragAndDrop />
        <br />
        <button type="button" className="mb-2" onClick={handleConfirm} disabled={isUploading || !isAbleToConfirm}>
          {
            isUploading 
              ? 'Uploading...'
              : 'Upload Logo and Continue'
          }
        </button>
        { 
          methods.formState.errors.file
            && 
              <p className="f-small f-italic pl-2">
                Error: { methods.formState.errors.file.message.toString() }
              </p>
        }
        { 
          methods.formState.errors.file_upload
            && 
              <p className="f-small f-italic pl-2">
                Error: { methods.formState.errors.file_upload.message.toString() }
              </p>
        }
      </form>
    </FormProvider>
  );
};

export default LogoUploaderAndColorPicker;
